
import Vue from 'vue';
import Undo20 from '@carbon/icons-vue/es/undo/20';
import Touch20 from '@carbon/icons-vue/es/touch--1/20';
import Cursor20 from '@carbon/icons-vue/es/cursor--1/20';
import Redo16 from '@carbon/icons-vue/es/redo/20';
import Add16 from '@carbon/icons-vue/es/add/20';
import Subtract16 from '@carbon/icons-vue/es/subtract/20';
import cloneDeep from 'lodash.clonedeep';
import { mapActions, mapState } from 'pinia';
import { useStore as useHistoryStore } from '@/stores/v2/history';
import { useStore as useProductStore } from '@/stores/v2/product';
import { useStore as useShapeStore } from '@/stores/v2/shape';
import { useStore as useMirrorStore } from '@/stores/v2/mirror';
import { useStore as useNotificationStore } from '@/stores/v2/notification';
import { generateEventPayloadV2 } from '@/utils/generatePriceParamsEventPayload';
import { mixin as clickaway } from 'vue-clickaway';

export default Vue.extend({
  components: {
    Undo20,
    Touch20,
    Cursor20,
    Redo16,
    Add16,
    Subtract16,
  },
  mixins: [clickaway],

  props: {
    zoomLevel: {
      type: Number,
      required: false,
      default: undefined,
    },
    fitToViewZoomLevel: {
      type: Number,
      required: false,
      default: undefined,
    },
  },

  data() {
    return {
      showZoomOptions: false,
      zoomStandardOptions: [
        10, 15, 20, 25, 50, 60, 75, 100, 125, 150, 200, 250, 300,
      ],
    };
  },

  computed: {
    ...mapState(useHistoryStore, ['isRedoPossible', 'isUndoPossible']),

    zoomOptions(): number[] {
      if (this.fitToViewZoomLevel) {
        return [
          ...this.zoomStandardOptions,
          this.getFormatedZoomLevel(this.fitToViewZoomLevel),
        ].sort((a, b) => a - b);
      }
      return this.zoomStandardOptions;
    },
  },

  methods: {
    ...mapActions(useProductStore, ['updateProductStateFromSnapshot']),
    ...mapActions(useShapeStore, ['updateShapeStateFromSnapshot']),
    ...mapActions(useNotificationStore, [
      'updateNotificationStateFromSnapshot',
    ]),
    ...mapActions(useHistoryStore, ['undo', 'redo']),
    ...mapActions(useMirrorStore, ['updateMirrorStateFromSnapshot']),

    onClickAway(): void {
      this.showZoomOptions = false;
    },

    handleZoomOptionChange(item: string): void {
      this.$emit('action:set-zoom-size', Number(item) / 100);
      this.showZoomOptions = false;
    },

    undoHandler(): void {
      if (this.isUndoPossible) {
        const stateSnapshot = this.undo();

        if (stateSnapshot) {
          if (stateSnapshot.productState !== undefined) {
            this.updateProductStateFromSnapshot(
              cloneDeep(stateSnapshot.productState)
            );
          }

          if (stateSnapshot.shapeState !== undefined) {
            this.updateShapeStateFromSnapshot(
              cloneDeep(stateSnapshot.shapeState)
            );
          }

          if (stateSnapshot.notificationState !== undefined) {
            this.updateNotificationStateFromSnapshot(
              cloneDeep(stateSnapshot.notificationState)
            );
          }

          if (stateSnapshot.mirrorState !== undefined) {
            this.updateMirrorStateFromSnapshot(
              cloneDeep(stateSnapshot.mirrorState)
            );
          }

          const eventPayload = generateEventPayloadV2();
          this.$emit('wizard:price-params-changed', {
            productPrintPositions: eventPayload,
          });
        }
      }
    },

    redoHandler(): void {
      if (this.isRedoPossible) {
        const stateSnapshot = this.redo();

        if (stateSnapshot) {
          if (stateSnapshot.productState !== undefined) {
            this.updateProductStateFromSnapshot(
              cloneDeep(stateSnapshot.productState)
            );
          }

          if (stateSnapshot.shapeState !== undefined) {
            this.updateShapeStateFromSnapshot(
              cloneDeep(stateSnapshot.shapeState)
            );
          }

          if (stateSnapshot.notificationState !== undefined) {
            this.updateNotificationStateFromSnapshot(
              cloneDeep(stateSnapshot.notificationState)
            );
          }

          if (stateSnapshot.mirrorState !== undefined) {
            this.updateMirrorStateFromSnapshot(
              cloneDeep(stateSnapshot.mirrorState)
            );
          }

          const eventPayload = generateEventPayloadV2();
          this.$emit('wizard:price-params-changed', {
            productPrintPositions: eventPayload,
          });
        }
      }
    },

    getFormatedZoomLevel(level: number): number {
      return Math.round(level * 100);
    },
  },
});
